import * as Yup from "yup";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
  INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY,
  MAX_LENGTH_100,
  REGEX_ONLY_NUMBER_AND_HYPHEN,
} from "../../../helpers/constants";
import { validFieldValidation } from "../../../helpers/constants/commonValidationSchemaUtils";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import { isNotEmpty } from "../../../helpers/utils";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  AddExistingProgramsSearchFormKey,
  AddProjectActivityFormKey,
  CreateProjectFormKey,
  ProjectAddOrEditExpenseOrFeeFormKey,
  ProjectFeeTabFormKey,
  ProjectInvoiceReversalFormKey,
  ProjectStatusFormKey,
  ProjectTabFormKey,
} from "./Models";

export const changeProjectStatusProgramSchema = Yup.object().shape({
  [ProjectStatusFormKey.status]: Yup.string().default(""),
  [ProjectStatusFormKey.date]: Yup.string().default(""),
  [ProjectStatusFormKey.notes]: Yup.string().default(""),
});

export const CreateProjectKeyDetailsSchema = (
  commonValidationMessages: KeyValueMap,
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
) =>
  Yup.object().shape({
    [CreateProjectFormKey.projectName]: Yup.string()
      .trim()
      .test(
        EXTENDED_SPECIAL_CHARS,
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
        (value) =>
          !value ||
          INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
      )
      .required(commonValidationMessages.requiredValidation),
    [CreateProjectFormKey.client]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProjectFormKey.billingOffice]: Yup.string().default(""),
    [CreateProjectFormKey.salesOfficeId]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProjectFormKey.clientBusinessUnit]: Yup.string().default(""),
    [CreateProjectFormKey.productLines]: Yup.array()
      .default([])
      .min(1, commonValidationMessages.requiredValidation),
    [CreateProjectFormKey.primaryProductLine]: Yup.string()
      .default("")
      .required(commonValidationMessages.requiredValidation),
    [CreateProjectFormKey.primaryDeliveryCountry]: Yup.string().default(""),
    [CreateProjectFormKey.primaryDeliveryOffice]: Yup.string()
      .default("")
      .required(commonValidationMessages.requiredValidation),
    [CreateProjectFormKey.poNumbers]: Yup.array().default([]),
    [CreateProjectFormKey.clientCostCenter]: Yup.string().default(""),
    [CreateProjectFormKey.projectFramework]: Yup.string()
      .default("")
      .required(commonValidationMessages.requiredValidation),
    [CreateProjectFormKey.dossierNumber]: Yup.string()
      .trim()
      .test(
        EXTENDED_SPECIAL_CHARS,
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
        (value) =>
          !value ||
          INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
      ),
  });

export const CreateProjectProjectTabSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape({
    [ProjectTabFormKey.billingOffice]: Yup.string().default(""),
    [ProjectTabFormKey.salesOfficeId]: Yup.string().default(""),
    [ProjectTabFormKey.clientRelationShipOwner]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProjectTabFormKey.billingContact]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProjectTabFormKey.hrContact]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProjectTabFormKey.projectManager]: Yup.array()
      .of(Yup.string().required())
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [ProjectTabFormKey.projectCoordinator]: Yup.array().default([]),
    [ProjectTabFormKey.salesCreditDetails]: Yup.array(),
  });

export const AddActivitySchema = (commonValidationMessages: KeyValueMap) =>
  Yup.object().shape({
    [AddProjectActivityFormKey.activityType]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [AddProjectActivityFormKey.modality]: Yup.string()
      .default("")
      .required(commonValidationMessages.requiredValidation),
    [AddProjectActivityFormKey.topic]: Yup.array()
      .of(Yup.string().required())
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [AddProjectActivityFormKey.notes]: Yup.string().default(""),
    [AddProjectActivityFormKey.onBehalfOfCoachId]: Yup.string().default(""),
    [AddProjectActivityFormKey.actualDurationInMinutes]:
      Yup.string().default(""),
    [AddProjectActivityFormKey.defaultDuration]: Yup.string().default(""),
    [AddProjectActivityFormKey.activityDate]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
  });

export const ProjectFeeTabSchema = (
  commonValidationMessages: KeyValueMap,
  earlyPayPercentageErrorMessage: string,
  minimumTransferFeeShouldBeLessThanProgramFee: string,
  programFeeShouldBeGreaterThanMinimumTransferFee: string,
) =>
  Yup.object().shape(
    {
      [ProjectFeeTabFormKey.id]: Yup.string().default(""),
      [ProjectFeeTabFormKey.billingTypeId]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),

      [ProjectFeeTabFormKey.billingTriggerId]: Yup.string().when(
        [ProjectFeeTabFormKey.billingTypeId],
        {
          is: (billingTypeId: string) =>
            ![
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling,
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
            ].includes(billingTypeId),
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [ProjectFeeTabFormKey.currencyId]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [ProjectFeeTabFormKey.fee]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [ProjectFeeTabFormKey.feeWithFormat]: Yup.string()
        .required(commonValidationMessages.requiredValidation)
        .test(
          "is-greater-than-minimum-transfer-fee",
          programFeeShouldBeGreaterThanMinimumTransferFee,
          (_, value) => {
            const programFeeValue = Number(value.parent?.fee ?? "0");
            const minimumTransferFeeValue = Number(
              value.parent?.minimumTransferFee ?? "0",
            );

            const proBonoCategoryId = value.parent?.proBonoCategoryId;
            const billingTypeId = value.parent?.billingTypeId;

            // If proBonoCategoryId has a value and billingTypeId is billingTypeImmediateBilling,
            // allow 0 or positive values only (skip comparison with minimumTransferFee)
            if (
              isNotEmpty(proBonoCategoryId) &&
              billingTypeId ===
                BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling
            ) {
              return programFeeValue >= 0;
            }

            return programFeeValue > minimumTransferFeeValue;
          },
        ),

      [ProjectFeeTabFormKey.earlyPayDays]: Yup.string(),
      [ProjectFeeTabFormKey.earlyPayPercentage]: Yup.string().test(
        "is-less-than-10",
        earlyPayPercentageErrorMessage,
        (value) => {
          if (value === undefined || value === null || value === "") {
            return true;
          }
          const numberValue = Number(value);
          return !isNaN(numberValue) && numberValue < 10;
        },
      ),
      [ProjectFeeTabFormKey.minimumTransferFeeWithFormat]: Yup.string()
        .optional()
        .test(
          "is-smaller-than-fee",
          minimumTransferFeeShouldBeLessThanProgramFee,
          (_, value) => {
            if (!value.parent?.minimumTransferFee) return true;
            const programFeeValue = Number(value.parent?.fee ?? "0");
            const minimumTransferFeeValue = Number(
              value.parent?.minimumTransferFee ?? "0",
            );
            const proBonoCategoryId = value.parent?.proBonoCategoryId;
            const billingTypeId = value.parent?.billingTypeId;

            // If proBonoCategoryId has a value and billingTypeId is billingTypeImmediateBilling,
            // allow 0 or positive values only (skip comparison with minimumTransferFee)
            if (
              isNotEmpty(proBonoCategoryId) &&
              billingTypeId ===
                BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling
            ) {
              return programFeeValue >= 0;
            }

            return programFeeValue > minimumTransferFeeValue;
          },
        ),
      [ProjectFeeTabFormKey.minimumTransferFee]: Yup.string().optional(),
      [ProjectFeeTabFormKey.proBonoCategoryId]: Yup.string().optional(),
      [ProjectFeeTabFormKey.schedulePeriod]: Yup.string().when(
        [ProjectFeeTabFormKey.billingTypeId],
        {
          is: (billingTypeId: string) =>
            [BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule].includes(
              billingTypeId,
            ),
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [ProjectFeeTabFormKey.feePaymentType]: Yup.string().when(
        [ProjectFeeTabFormKey.billingTypeId],
        {
          is: (billingTypeId: string) =>
            [
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
            ].includes(billingTypeId),
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [ProjectFeeTabFormKey.schedules]: Yup.string().optional(),
      [ProjectFeeTabFormKey.milestones]: Yup.string().optional(),
      [ProjectFeeTabFormKey.feePercentage]: Yup.string().optional(),
      [ProjectFeeTabFormKey.amount]: Yup.string().optional(),
      [ProjectFeeTabFormKey.amountWithFormat]: Yup.string().optional(),
      [ProjectFeeTabFormKey.scheduleFrequency]: Yup.string().optional(),

      [ProjectFeeTabFormKey.billingSchedules]: Yup.array(),
      [ProjectFeeTabFormKey.description]: Yup.string().optional(),
      [ProjectFeeTabFormKey.status]: Yup.string().optional(),
      [ProjectFeeTabFormKey.taxSearch]: Yup.string().optional(),
      [ProjectFeeTabFormKey.tax]: Yup.string().optional(),
      [ProjectFeeTabFormKey.taxAmount]: Yup.string().optional(),
      [ProjectFeeTabFormKey.taxAmountWithFormat]: Yup.string().optional(),
      [ProjectFeeTabFormKey.taxEntries]: Yup.array(),
    },
    [
      [
        ProjectFeeTabFormKey.feeWithFormat,
        ProjectFeeTabFormKey.minimumTransferFeeWithFormat,
      ],
    ],
  );

export const ProjectAddOrEditExpenseOrFeeSchema = (
  commonValidationMessages: KeyValueMap,
) => {
  const {
    projectFeeOrExpenseName,
    projectType,
    projectTransactionDate,
    projectFeeTypeId,
    projectFeeOrExpenseCurrencyId,
    projectFeeOrExpenseAmountWithFormat,
    projectBillingCurrency,
    projectBillingCurrencyExchangeRate,
    projectComments,
    projectExpenseIncurredById,
    projectExpenseCategoryId,
    projectExpenseUnitPrice,
    projectExpenseQuantity,
    projectExpenseTypeId,
  } = ProjectAddOrEditExpenseOrFeeFormKey;
  return Yup.object().shape({
    [projectType]: Yup.string(),
    [projectFeeOrExpenseName]: Yup.string()
      .required(commonValidationMessages.requiredValidation)
      .test(
        EXTENDED_SPECIAL_CHARS,
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        (value) =>
          !value ||
          INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
      ),
    [projectTransactionDate]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    // fee details fields
    [projectFeeTypeId]: Yup.string().when([projectType], {
      is: (projectType: string) =>
        ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE ===
        projectType,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [projectFeeOrExpenseCurrencyId]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [projectFeeOrExpenseAmountWithFormat]: Yup.string().when([projectType], {
      is: (projectType: string) =>
        ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE ===
        projectType,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    // billing details
    [projectBillingCurrency]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [projectBillingCurrencyExchangeRate]: Yup.string().when(
      [projectFeeOrExpenseCurrencyId, projectBillingCurrency],
      {
        is: (
          projectFeeOrExpenseCurrencyId: string,
          projectBillingCurrency: string,
        ) => {
          if (
            projectFeeOrExpenseCurrencyId &&
            projectFeeOrExpenseCurrencyId !== projectBillingCurrency
          ) {
            return true;
          }
          return false;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.optional(),
      },
    ),
    // comments
    [projectComments]: Yup.string().optional(),
    // expense fields
    [projectExpenseIncurredById]: Yup.string().optional(),
    [projectExpenseCategoryId]: Yup.string().when(projectType, {
      is: (projectType: string) =>
        ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE === projectType,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [projectExpenseCategoryId]: Yup.string().when(projectType, {
      is: (projectType: string) =>
        ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE === projectType,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [projectExpenseQuantity]: Yup.string()
      .when(projectType, {
        is: (projectType: string) =>
          ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE === projectType,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.optional(),
      })

      .test(
        "greater-than-zero",
        commonValidationMessages.greaterThanZeroFieldValidator,
        (value) => {
          return !value || Number(value) > 0;
        },
      ),
    [projectExpenseTypeId]: Yup.string().when(projectType, {
      is: (projectType: string) =>
        ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE === projectType,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [projectExpenseUnitPrice]: Yup.string()
      .when(projectType, {
        is: (projectType: string) =>
          ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE === projectType,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.optional(),
      })
      .test(
        "greater-than-zero",
        commonValidationMessages.greaterThanZeroFieldValidator,
        (value) => {
          return !value || Number(value) > 0;
        },
      ),
  });
};

export const ProjectInvoiceReverseValidationSchema = (
  commonValidationMessages: KeyValueMap,
) => {
  return Yup.object().shape({
    [ProjectInvoiceReversalFormKey.projectReversalReason]:
      Yup.string().required(commonValidationMessages.requiredValidation),
    [ProjectInvoiceReversalFormKey.projectComments]: Yup.string()
      .max(
        MAX_LENGTH_100,
        commonValidationMessages.maxLengthValidation ||
          "Max 100 characters are allowed.",
      )
      .when(ProjectInvoiceReversalFormKey.projectReversalReason, {
        is: (val: string) =>
          val === INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
};

export const AddExistingProgramsSearchSchema = (
  commonValidationMessages: KeyValueMap,
) => {
  return Yup.object().shape({
    [AddExistingProgramsSearchFormKey.projectManagerId]:
      Yup.string().default(""),
    [AddExistingProgramsSearchFormKey.programParticipantName]:
      Yup.string().default(""),
    [AddExistingProgramsSearchFormKey.programParticipantNameFilter]:
      Yup.string().default(""),
    [AddExistingProgramsSearchFormKey.keyNumber]: validFieldValidation(
      commonValidationMessages.alphabetOrSpecialCharNotAllowed,
      REGEX_ONLY_NUMBER_AND_HYPHEN,
    ).default(""),
    [AddExistingProgramsSearchFormKey.keyNumberFilter]:
      Yup.string().default(""),
    [AddExistingProgramsSearchFormKey.clientManagedStatus]:
      Yup.string().default(""),
    [AddExistingProgramsSearchFormKey.programStatus]: Yup.string().default(""),
  });
};
